// NOTICE: KAPTION-INTEGRATION
// Favorites (sidebar) are always scoped to the current account.
// There are two layers:
// 1) Account favorites (global, stored on Account.settings)
// 2) Personal favorites (per-user, stored on User.ui_settings)

import { computed, ref, watch } from 'vue';
import {
  useStore,
  useFunctionGetter,
  useMapGetter,
} from 'dashboard/composables/store';
import { useUISettings } from './useUISettings';
import { useAdmin } from './useAdmin';

const normalizeFavorite = item => {
  if (!item || typeof item !== 'object') return null;
  const { type, id } = item;
  if (!type || id == null) return null;
  return { type, id };
};

const uniqFavorites = favorites => {
  const out = [];
  (favorites || []).forEach(item => {
    const normalized = normalizeFavorite(item);
    if (!normalized) return;
    if (!out.some(i => i.type === normalized.type && i.id === normalized.id)) {
      out.push(normalized);
    }
  });
  return out;
};

export function useKaptionFavorites() {
  const store = useStore();
  const { uiSettings, updateUISettings } = useUISettings();
  const { isAdmin } = useAdmin();
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const currentAccount = useFunctionGetter(
    'accounts/getAccount',
    currentAccountId
  );
  const hasMigratedLegacyForAccount = ref(false);

  const accountFavorites = computed(() => {
    const raw = currentAccount.value?.settings?.favorite_sidebar_items;
    return uniqFavorites(Array.isArray(raw) ? raw : []);
  });

  // Personal favorites are stored per-user per-account.
  // We intentionally do NOT fallback to any global (cross-account) key.
  const personalFavorites = computed(() => {
    const accountId = currentAccountId.value;
    if (accountId == null) return [];
    const byAccount = uiSettings.value.starred_sidebar_items_by_account;
    const list = byAccount?.[accountId];
    return uniqFavorites(Array.isArray(list) ? list : []);
  });

  // Legacy migration: if the user has old global favorites, migrate them to the
  // current account only (so favorites are strictly account-scoped).
  watch(
    [currentAccountId, uiSettings],
    ([accountId, settings]) => {
      if (hasMigratedLegacyForAccount.value) return;
      if (accountId == null) return;

      const byAccount = settings?.starred_sidebar_items_by_account;
      const legacy = settings?.starred_sidebar_items;

      if (
        byAccount &&
        Object.prototype.hasOwnProperty.call(byAccount, accountId)
      )
        return;
      if (!Array.isArray(legacy) || legacy.length === 0) return;

      hasMigratedLegacyForAccount.value = true;

      updateUISettings({
        starred_sidebar_items_by_account: {
          ...(byAccount || {}),
          [accountId]: uniqFavorites(legacy),
        },
        // Clear legacy to prevent it from leaking into other accounts
        starred_sidebar_items: [],
      });
    },
    { immediate: true }
  );

  const isAccountFavorite = (type, id) =>
    accountFavorites.value.some(i => i.type === type && i.id === id);
  const isPersonalFavorite = (type, id) =>
    personalFavorites.value.some(i => i.type === type && i.id === id);
  const isFavorite = (type, id) =>
    isAccountFavorite(type, id) || isPersonalFavorite(type, id);

  const setPersonalFavorite = (type, id, value) => {
    const accountId = currentAccountId.value;
    if (accountId == null) return;

    const byAccount = uiSettings.value.starred_sidebar_items_by_account || {};
    const existing = uniqFavorites(byAccount[accountId] || []);
    const next = value
      ? uniqFavorites([...existing, { type, id }])
      : existing.filter(i => !(i.type === type && i.id === id));

    updateUISettings({
      starred_sidebar_items_by_account: {
        ...byAccount,
        [accountId]: next,
      },
    });
  };

  const togglePersonalFavorite = (type, id) => {
    setPersonalFavorite(type, id, !isPersonalFavorite(type, id));
  };

  const setPersonalFavorites = favorites => {
    const accountId = currentAccountId.value;
    if (accountId == null) return;

    const byAccount = uiSettings.value.starred_sidebar_items_by_account || {};

    updateUISettings({
      starred_sidebar_items_by_account: {
        ...byAccount,
        [accountId]: uniqFavorites(favorites),
      },
    });
  };

  const clearPersonalFavorites = () => setPersonalFavorites([]);

  const setAccountFavorite = async (type, id, value) => {
    if (!isAdmin.value) return;
    const existing = accountFavorites.value;
    const next = value
      ? uniqFavorites([...existing, { type, id }])
      : existing.filter(i => !(i.type === type && i.id === id));

    await store.dispatch('accounts/update', {
      options: { silent: true },
      favorite_sidebar_items: next,
    });
  };

  const toggleAccountFavorite = async (type, id) => {
    await setAccountFavorite(type, id, !isAccountFavorite(type, id));
  };

  const mergedFavorites = computed(() => {
    return uniqFavorites([
      ...accountFavorites.value,
      ...personalFavorites.value,
    ]);
  });

  return {
    // lists
    accountFavorites,
    personalFavorites,
    mergedFavorites,

    // checks
    isAccountFavorite,
    isPersonalFavorite,
    isFavorite,

    // mutations
    setPersonalFavorite,
    setPersonalFavorites,
    clearPersonalFavorites,
    togglePersonalFavorite,
    setAccountFavorite,
    toggleAccountFavorite,
  };
}
